import { useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import { Box, Card, CardContent, Typography, Stack, Button, CircularProgress } from '@mui/material';
import { card, btn, typo } from '../styles/ds';
import authFetch, { responseError } from '../utils/authFetch';
import { websiteRequest, hasHiddurImages } from './websiteApi';

// The server accepts up to 2MB (backend services/websiteImages.js). Photos are
// shrunk here first, so a phone picture uploads as a ~200KB file.
const MAX_SIDE = 1600;
const MAX_BYTES = 2 * 1024 * 1024;
const HIDDUR_ORDER = ['standard', 'mehudar', 'premium'];

const toBlob = (canvas, type, quality) => new Promise(resolve => canvas.toBlob(resolve, type, quality));

/** Longest side ≤ 1600px, WebP (JPEG where the browser can't encode WebP). */
async function shrink(file) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff'; // transparent PNG areas → white, not black, in JPEG
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  for (const quality of [0.85, 0.7, 0.55]) {
    let blob = await toBlob(canvas, 'image/webp', quality);
    if (!blob || blob.type !== 'image/webp') blob = await toBlob(canvas, 'image/jpeg', quality);
    if (blob && blob.size <= MAX_BYTES) return blob;
  }
  throw new Error('התמונה גדולה מדי גם אחרי כיווץ');
}

/** One image slot: the main image (hiddur null) or one Hiddur level's. */
const ImageSlot = ({ product, hiddur, imageId, label, readOnly, onSaved }) => {
  const notify = useNotify();
  const input = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const level = hiddur ? `hiddur=${hiddur}` : '';

  useEffect(() => {
    if (!imageId) { setPreview(null); return undefined; }
    let url = null;
    let cancelled = false;
    authFetch(`/website_products/${product.id}/image${level ? `?${level}` : ''}`)
      .then(res => (res.ok ? res.blob() : null))
      .then(blob => { if (blob && !cancelled) { url = URL.createObjectURL(blob); setPreview(url); } })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [product.id, imageId, level]);

  const upload = async file => {
    if (!file) return;
    setBusy(true);
    try {
      const blob = await shrink(file);
      const res = await authFetch(`/website_products/${product.id}/image?version=${product.version}${level ? `&${level}` : ''}`, {
        method: 'PUT', headers: { 'Content-Type': blob.type }, body: blob,
      });
      if (!res.ok) throw new Error(await responseError(res, 'שגיאה בהעלאת התמונה'));
      notify('התמונה נשמרה', { type: 'success' });
      onSaved(await res.json());
    } catch (err) {
      notify(err.name === 'InvalidStateError' || err.name === 'EncodingError' ? 'לא ניתן לקרוא את הקובץ כתמונה' : err.message, { type: 'error' });
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      onSaved(await websiteRequest(`/website_products/${product.id}/image`, 'DELETE', { version: product.version, ...(hiddur ? { hiddur } : {}) }));
      notify('התמונה הוסרה', { type: 'success' });
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setBusy(false); }
  };

  return (
    <Stack spacing={1} alignItems="center" sx={{ width: 160 }}>
      <Typography fontSize={13} fontWeight={600} dir={hiddur ? 'ltr' : undefined}>{label}</Typography>
      <Box sx={{
        width: 160, height: 160, borderRadius: 1, border: '1px solid', borderColor: 'divider',
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: 'grey.50',
      }}>
        {busy ? <CircularProgress size={28} />
          : preview ? <img src={preview} alt={label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            : <Typography fontSize={13} color="text.secondary">אין תמונה</Typography>}
      </Box>
      {!readOnly && (
        <Stack direction="row" spacing={1}>
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" hidden
            onChange={e => upload(e.target.files?.[0])} />
          <Button size="small" variant="contained" disabled={busy} onClick={() => input.current?.click()} sx={btn.primary}>
            {imageId ? 'החלפה' : 'העלאה'}
          </Button>
          {imageId && <Button size="small" variant="outlined" disabled={busy} onClick={remove} sx={btn.cancel}>הסרה</Button>}
        </Stack>
      )}
    </Stack>
  );
};

/**
 * The listing's website images: a main image (migration 038) and, on Mezuzah /
 * Megillah / Gasot listings, one per Hiddur level (migration 039).
 */
const WebsiteProductImage = ({ product, readOnly, onSaved }) => {
  const levels = hasHiddurImages(product.family) ? HIDDUR_ORDER : [];
  const hiddurLabel = code => product.options?.find(o => o.key === 'hiddur')?.values.find(v => v.code === code)?.label_en || code;
  return (
    <Card sx={card.section}>
      <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography sx={{ ...typo.sectionTitle, mb: 0.5 }}>תמונות המוצר באתר</Typography>
        <Typography sx={{ ...typo.meta, mb: 2 }}>
          {levels.length
            ? 'תמונה ראשית, ותמונה לכל רמת הידור: בעמוד המוצר התמונה מתחלפת לפי הרמה שהלקוח בוחר. בלי תמונה מוצגת התמונה הכללית של סוג המוצר.'
            : 'תמונה אחת למוצר. בלי תמונה, האתר מציג את התמונה הכללית של סוג המוצר.'}
          {' '}התמונה מוקטנת ומכווצת אוטומטית לפני ההעלאה (JPG, PNG או WebP).
        </Typography>
        <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap">
          <ImageSlot product={product} hiddur={null} imageId={product.image_id} label="תמונה ראשית"
            readOnly={readOnly} onSaved={onSaved} />
          {levels.map(code => (
            <ImageSlot key={code} product={product} hiddur={code} imageId={product.hiddur_images?.[code]}
              label={hiddurLabel(code)} readOnly={readOnly} onSaved={onSaved} />
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default WebsiteProductImage;
